/**
 * Search index manager for fast contact search
 * Builds and maintains a search index for instant typeahead
 * Searches based on surname and given name with Latvian normalization
 */

import { ContactStore, Contact } from './ContactStore';
import { normalizeForSearch } from '../utils/latvianNormalizer';

export interface SearchIndexEntry {
  id: string;
  givenName: string;
  surname: string;
  displayName: string; // Keep for display purposes
  emailPrimary: string;
  company: string;
  title: string;
  searchableText: string; // Combined searchable text
  givenNameTokens: string[]; // Tokenized given name for prefix matching
  surnameTokens: string[]; // Tokenized surname for prefix matching
  emailTokens: string[]; // Tokenized email for prefix matching
}

export interface SearchResult {
  contact: Contact;
  score: number;
  matchType: 'name' | 'email' | 'company' | 'title' | 'combined';
  matchedText: string;
}

export class SearchIndexManager {
  private store: ContactStore;
  private index: Map<string, SearchIndexEntry> = new Map();
  private isIndexBuilt = false;
  private isOutlookContext: boolean;

  constructor(store: ContactStore, isOutlookContext: boolean = true) {
    this.store = store;
    this.isOutlookContext = isOutlookContext;
  }

  /**
   * Build search index from all contacts
   */
  async buildIndex(): Promise<void> {
    console.log('Building search index with Latvian normalization...');
    const startTime = Date.now();
    
    const contacts = await this.store.getAllContacts();
    this.index.clear();

    let indexedCount = 0;
    for (const contact of contacts) {
      // Only index contacts that have email addresses
      if (this.hasEmailAddress(contact)) {
        const entry = this.createIndexEntry(contact);
        this.index.set(contact.id, entry);
        indexedCount++;
      }
    }

    this.isIndexBuilt = true;
    
    // Save index to IndexedDB
    const indexData = JSON.stringify(Array.from(this.index.entries()));
    await this.store.setSearchIndex(indexData);
    
    const buildTime = Date.now() - startTime;
    console.log(`Search index built in ${buildTime}ms for ${indexedCount} contacts with emails (${contacts.length} total contacts) using Latvian normalization`);
  }

  /**
   * Force rebuild the search index (useful after normalization changes)
   */
  async forceRebuild(): Promise<void> {
    console.log('Force rebuilding search index...');
    this.isIndexBuilt = false;
    await this.buildIndex();
  }

  /**
   * Update index with new/updated contacts
   */
  async updateIndex(contacts: Contact[]): Promise<void> {
    if (!this.isIndexBuilt) {
      await this.loadIndex();
    }

    for (const contact of contacts) {
      // Only index contacts that have email addresses
      if (this.hasEmailAddress(contact)) {
        const entry = this.createIndexEntry(contact);
        this.index.set(contact.id, entry);
      } else {
        // Remove from index if contact no longer has email
        this.index.delete(contact.id);
      }
    }

    // Save updated index
    const indexData = JSON.stringify(Array.from(this.index.entries()));
    await this.store.setSearchIndex(indexData);
  }

  /**
   * Remove contacts from index
   */
  async removeFromIndex(contactIds: string[]): Promise<void> {
    if (!this.isIndexBuilt) {
      await this.loadIndex();
    }

    for (const id of contactIds) {
      this.index.delete(id);
    }

    // Save updated index
    const indexData = JSON.stringify(Array.from(this.index.entries()));
    await this.store.setSearchIndex(indexData);
  }

  /**
   * Load index from IndexedDB
   */
  async loadIndex(): Promise<void> {
    try {
      const indexRecord = await this.store.getSearchIndex();
      if (indexRecord && indexRecord.data) {
        const entries = JSON.parse(indexRecord.data) as [string, SearchIndexEntry][];
        this.index = new Map(entries);
        this.isIndexBuilt = true;
        console.log(`Loaded search index with ${this.index.size} entries`);
      } else {
        // No index exists, build it
        await this.buildIndex();
      }
    } catch (error) {
      console.error('Failed to load search index:', error);
      // Fallback: build index from scratch
      await this.buildIndex();
    }
  }

  /**
   * Search contacts with instant typeahead
   */
  async search(query: string): Promise<SearchResult[]> {
    if (!this.isIndexBuilt) {
      await this.loadIndex();
    }

    if (!query || query.trim().length < 2) {
      return [];
    }

    const normalizedQuery = normalizeForSearch(query).trim();
    const results: SearchResult[] = [];

    const promises: Promise<SearchResult | null>[] = [];
    
    this.index.forEach((entry, id) => {
      const score = this.calculateScore(entry, normalizedQuery);
      if (score > 0) {
        promises.push(
          this.store.getContact(id).then(contact => {
            if (contact && !contact.deleted && this.hasEmailAddress(contact)) {
              return {
                contact,
                score,
                matchType: this.getMatchType(entry, normalizedQuery),
                matchedText: this.getMatchedText(entry, normalizedQuery),
              };
            }
            return null;
          })
        );
      }
    });

    // Wait for all contact lookups to complete
    const searchResults = await Promise.all(promises);
    const validResults = searchResults.filter((result): result is SearchResult => result !== null);

    // Sort by score (highest first)
    return validResults
      .sort((a, b) => b.score - a.score);
  }

  /**
   * Create search index entry for a contact
   */
  private createIndexEntry(contact: Contact): SearchIndexEntry {
    const searchableText = [
      contact.givenName,
      contact.surname,
      contact.emailPrimary,
      contact.company,
      contact.title,
      ...contact.emails,
    ].filter(Boolean).join(' ');
    
    const normalizedSearchableText = normalizeForSearch(searchableText);

    return {
      id: contact.id,
      givenName: contact.givenName,
      surname: contact.surname,
      displayName: contact.displayName,
      emailPrimary: contact.emailPrimary,
      company: contact.company,
      title: contact.title,
      searchableText: normalizedSearchableText,
      givenNameTokens: this.tokenize(contact.givenName),
      surnameTokens: this.tokenize(contact.surname),
      emailTokens: this.tokenize(contact.emailPrimary),
    };
  }

  /**
   * Tokenize text for prefix matching
   */
  private tokenize(text: string): string[] {
    if (!text) return [];
    
    return normalizeForSearch(text)
      .split(/\s+/)
      .filter(token => token.length > 0)
      .flatMap(token => {
        // Generate prefixes for each token
        const prefixes = [];
        for (let i = 1; i <= token.length; i++) {
          prefixes.push(token.substring(0, i));
        }
        return prefixes;
      });
  }

  /**
   * Calculate search score for a contact
   */
  private calculateScore(entry: SearchIndexEntry, query: string): number {
    let score = 0;

    const normalizedGivenName = normalizeForSearch(entry.givenName);
    const normalizedSurname = normalizeForSearch(entry.surname);

    // Given name match (highest priority)
    if (normalizedGivenName.includes(query)) {
      score += 100;
      if (normalizedGivenName.startsWith(query)) {
        score += 50; // Prefix match bonus
      }
    }

    // Surname match (highest priority)
    if (normalizedSurname.includes(query)) {
      score += 100;
      if (normalizedSurname.startsWith(query)) {
        score += 50; // Prefix match bonus
      }
    }

    // Exact email match
    if (entry.emailPrimary.toLowerCase().includes(query)) {
      score += 80;
      if (entry.emailPrimary.toLowerCase().startsWith(query)) {
        score += 40; // Prefix match bonus
      }
    }

    // Company match
    if (entry.company.toLowerCase().includes(query)) {
      score += 100;
      if (entry.company.toLowerCase().startsWith(query)) {
        score += 50; // Prefix match bonus
      }
    }

    // Title match
    if (entry.title.toLowerCase().includes(query)) {
      score += 40;
    }

    // Token-based prefix matching
    const queryTokens = query.split(/\s+/);
    for (const queryToken of queryTokens) {
      if (entry.givenNameTokens.includes(queryToken)) {
        score += 20;
      }
      if (entry.surnameTokens.includes(queryToken)) {
        score += 20;
      }
      if (entry.emailTokens.includes(queryToken)) {
        score += 15;
      }
    }

    // Combined searchable text match
    if (entry.searchableText.includes(query)) {
      score += 10;
    }

    return score;
  }

  /**
   * Determine the type of match
   */
  private getMatchType(entry: SearchIndexEntry, query: string): SearchResult['matchType'] {
    const normalizedGivenName = normalizeForSearch(entry.givenName);
    const normalizedSurname = normalizeForSearch(entry.surname);
    
    if (normalizedGivenName.includes(query) || normalizedSurname.includes(query)) return 'name';
    if (entry.emailPrimary.toLowerCase().includes(query)) return 'email';
    if (entry.company.toLowerCase().includes(query)) return 'company';
    if (entry.title.toLowerCase().includes(query)) return 'title';
    return 'combined';
  }

  /**
   * Get the matched text for highlighting
   */
  private getMatchedText(entry: SearchIndexEntry, query: string): string {
    const normalizedGivenName = normalizeForSearch(entry.givenName);
    const normalizedSurname = normalizeForSearch(entry.surname);
    
    if (normalizedGivenName.includes(query)) return entry.givenName;
    if (normalizedSurname.includes(query)) return entry.surname;
    if (entry.emailPrimary.toLowerCase().includes(query)) return entry.emailPrimary;
    if (entry.company.toLowerCase().includes(query)) return entry.company;
    if (entry.title.toLowerCase().includes(query)) return entry.title;
    return entry.searchableText;
  }

  /**
   * Get index statistics
   */
  getIndexStats(): {
    isBuilt: boolean;
    entryCount: number;
    lastUpdated: string | null;
  } {
    return {
      isBuilt: this.isIndexBuilt,
      entryCount: this.index.size,
      lastUpdated: this.isIndexBuilt ? new Date().toISOString() : null,
    };
  }

  /**
   * Clear the search index
   */
  async clearIndex(): Promise<void> {
    this.index.clear();
    this.isIndexBuilt = false;
    await this.store.setSearchIndex('');
  }

  /**
   * Check if a contact has any email addresses (Outlook only)
   */
  private hasEmailAddress(contact: Contact): boolean {
    // For Teams, include all contacts regardless of email
    if (!this.isOutlookContext) {
      return true;
    }
    
    // For internal users in Outlook, allow them even without email addresses
    if (contact.sourceType === 'internal') {
      return true; // Include all internal users regardless of email
    }
    
    return !!(contact.emailPrimary && contact.emailPrimary.trim()) || 
           (contact.emails && contact.emails.length > 0 && contact.emails.some(email => email && email.trim()));
  }
}
